import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, KeyValueDiffer, KeyValueDiffers, OnDestroy, OnInit, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Store } from '@ngrx/store';
import { I18NextPipe } from "angular-i18next";
import { Observable, Subject, Subscription } from 'rxjs';
import { DialogDownloadTableComponent } from "src/app/common/dialogs/dialog-download-table/dialog-download-table.component";
import { report } from 'src/app/dashboards/models';
import { State } from 'src/app/dashboards/state';
import { WidgetBaseComponent } from 'src/app/dashboards/widget/widgets/widget-base.component';
import { widgetType } from 'src/app/enums';
import { gridItem } from 'src/app/models';
import { CommonService } from 'src/app/services/services/common.service';
import { DashboardService } from "src/app/services/services/dashboard.service";

@Component({
    selector: 'app-web-engage-reports-viualization',
    templateUrl: './web-engage-reports-viualization.component.html',
    styleUrls: ['./web-engage-reports-viualization.component.scss'],
    animations: [
        trigger('collapse', [
            state('false', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
            state('true', style({ height: '0', visibility: 'hidden' })),
            transition('false => true', animate('200ms ease-in')),
            transition('true => false', animate('200ms ease-out'))
        ])
    ],
    standalone: false
})

export class WebEngageReportsVisualizationComponent implements OnInit, OnDestroy, WidgetBaseComponent {
  @Input() public isInWizard: boolean;
  @Input() public report: report | any;
  @Input() rdp_data: any;
  @Input() isReportModified: boolean;
  @Input() canExport: boolean;

  _gridItem: gridItem;
  get gridItem(): gridItem {
    return this._gridItem;
  }

  @Input() set gridItem(value: any) {
    this._gridItem = value;
    setTimeout(() => {
      this.notifier.next();
    }, 0);
  }

  _colorSchemeId: number;
  get colorSchemeId(): number {
    return this._colorSchemeId;
  }
  @Input() set colorSchemeId(value: number) {
    this._colorSchemeId = value;
  }

  @Input() viewReportObservable$: Observable<void>;
  @Input() orderColumnsNotifier$: Subject<boolean>;

  @Output() public reportFullscreenNotifier = new EventEmitter<boolean>();
  @Output() public refreshNotifier = new EventEmitter<boolean>();
  @Output() public showColumnButtonNotifier = new EventEmitter<boolean>();
  @Output() public exportReportNotifier = new EventEmitter();
  @Output() rdp_dataChange = new EventEmitter<any>();

  public terminate$ = new Subject<void>();
  public notifier: Subject<void> = new Subject<void>();
  public downloadDialogNotifier: Subject<void> = new Subject<void>();
  generateNotifier = new Subject<void>();
  viewReportSubject$: Subject<any> = new Subject<void>();
  dataLoadingSubject$: Subject<boolean> = new Subject<boolean>();
  public type: string = "RuleMatch";
  public widgetType = widgetType;

  reportInfo: string;
  isReportFullscreen: boolean;
  showReport: boolean;
  private viewReportSubscription: Subscription;
  private rdp_dataDiffer: KeyValueDiffer<string, any>;

  constructor(private dashboardsService: DashboardService, private store: Store<State>, private i18nextPipe: I18NextPipe, public dialog: MatDialog,
    private differs: KeyValueDiffers, private commonService: CommonService) { }

  ngOnInit(): void {
    this.viewReportSubscription = this.viewReportObservable$.subscribe(() => this.viewReport());
    this.rdp_dataDiffer = this.differs.find(this.rdp_data).create();
  }

  ngOnDestroy(): void {
    this.terminate$.next();
    this.terminate$.complete();
    this.viewReportSubscription.unsubscribe();
  }

  // rdp_data is changed on deeper level so it cannot be detected by ngOnChanges
  ngDoCheck(): void {
    const changes = this.rdp_dataDiffer.diff(this.rdp_data);

    if (changes) {
      this.rdp_dataChange.emit(this.rdp_data);
    }
  }

  toggleReportFullscreen() {
    this.isReportFullscreen = !this.isReportFullscreen;
    // keep this line so the workaround ("resize") fix in base-reports-details executes
    this.reportFullscreenNotifier.emit(this.isReportFullscreen);
  }

  fullscreenReport() {
    this.isReportFullscreen = true;
    // keep this line so the workaround ("resize") fix in base-reports-details executes
    this.reportFullscreenNotifier.emit(this.isReportFullscreen);
  }

  viewReport() {
    this.showReport = true;
    this.dataLoadingSubject$.next(true);
    this.fullscreenReport();
    this.showColumnButtonNotifier.emit(true);

    this.dashboardsService.getWebEngageReportResult(this.gridItem.setup).subscribe((result) => {
      let columnIdsFromSettings: string[] = this.gridItem.setup.columns;
      let data = result.data;
      let columns = columnIdsFromSettings.map(id => result.columns.find(c => c.id === id));
      if(result?.specialReportProp?.hideUniqueCount){
        columns = columns.filter(c => c.id !== "u_count");
      }
      this.reportInfo = this.commonService.reportInfoLine(this.gridItem, result.reportInfo.lastReportTime, this.gridItem.setup.serviceNumber ?? "");

      this.viewReportSubject$.next({ data, columns });
      this.dataLoadingSubject$.next(false);
    },
    (error: HttpErrorResponse) => {
      // TODO
    });
  }

  setPeriod(newPeriod) {
    this.gridItem.setup.period = newPeriod;
  }

  exportReport() {
    this.exportReportNotifier.emit();
  }

  openDownloadDialog() {
    this.downloadDialogNotifier.next();
  }

  // for testing purposes - handler for "Download Report 2" button
  exportTable2() {
    const dialogRef = this.dialog.open(DialogDownloadTableComponent, {
      panelClass: 'dialog-standard-pannel',
      backdropClass: "backdrop-invisible",
      data: {
        fileName: (this.report as any).report_Code,
        reportCode: (this.report as any).report_Code,
        fileType: "XLSX",
        fileTypes: ["XLSX", "CSV (Comma Delimmited)"],
        isChart: false
      },
      width: "495px",
      height: "305px"
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {

        //let fileName = "RuleMatch";
        let fileName : string = data.fileName;
        let fileType: string = data.fileType;
        let isTotalChecked: boolean = data.isTotalChecked;
        let reportTitle : string = this.report?.shortDescription;

        //let model = {
        //  data: [...exportData],
        //  exportColumns: exportColumns,
        //  reportInfo: this.reportInfo,
        //  reportTitle: reportTitle
        //};

        //if (fileType.toLowerCase() !== "xlsx" && model.data.filter(i => i["Agent ID"] === "Total").length > 0) {
        //  model.data = model.data.filter(i => i["Agent ID"] !== "Total");
        //}

        this.dashboardsService.ExportWebEngageReportToFile(this.gridItem.setup, reportTitle, fileType, isTotalChecked).subscribe(blob => {
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement("a");

          link.href = blobUrl;
          link.download = fileType.toLowerCase() === "xlsx" ? `${fileName}.xlsx` : `${fileName}.csv`;

          document.body.appendChild(link);

          link.dispatchEvent(
            new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window
            })
          );

          document.body.removeChild(link);
        });
      }
    });

    //let fileName = "RuleMatch";
    //let reportTitle = this.report?.shortDescription;
    //this.dashboardsService.ExportWebEngageReportToFile(this.gridItem.setup, reportTitle).subscribe(blob => {
    //  const blobUrl = URL.createObjectURL(blob);
    //  const link = document.createElement("a");

    //  link.href = blobUrl;
    //  link.download = `${fileName}.xlsx`;

    //  document.body.appendChild(link);

    //  link.dispatchEvent(
    //    new MouseEvent('click', {
    //      bubbles: true,
    //      cancelable: true,
    //      view: window
    //    })
    //  );

    //  document.body.removeChild(link);
    //});
  }
}
